import React, { useRef } from "react";
import { Box, Button, Flyout, SelectOption } from "@showitinc/showit-wc";

import Input from "@/app2components/base/Input";
import { SELECT } from "@/constants/InputConstants.mjs";
import { MIXED } from "@/constants/DesignConstants.mjs";
import DynamicInput from "@/app2components/base/DynamicInput";
/**
 * @typedef {import("immutable").Map} ImmutableMap
 * @typedef {typeof import("@/constants/DesignConstants.mjs").LAYOUTS} LAYOUTS
 */

const SHADOW_STYLE_INNER = "inner";
const SHADOW_STYLE_NONE = "none";
const SHADOW_STYLE_OUTER = "outer";

/**
 * Drop shadow property panel. Supports dynamic input data.

/**
 * @param {Object} props
 * @param {boolean=} props.disableInsetShadow - Flag to disable inset shadow option.
 * @param {boolean} props.isLocked - Indicates if the input is locked.
 * @param {string} props.label - The label for the input.
 * @param {LAYOUTS[number]} props.layout - The layout context.
 * @param {Function} props.onSIChange - Handler for change events.
 * @param {Object} props.subProps - Sub-properties for the shadow.
 * @param {*} props.value - The current value of the shadow style.

 * @returns {JSX.Element}
 */
const DropShadowProperties = ({
  disableInsetShadow = false,
  isLocked,
  label,
  layout,
  onSIChange,
  subProps,
  value,
  shift = "left",
}) => {
  const flyoutRef = useRef();
  const inputRef = useRef();

  const autoOpen = (value) => {
    if ([SHADOW_STYLE_INNER, SHADOW_STYLE_OUTER].includes(value)) {
      flyoutRef.current.showPopover(inputRef.current);
    }
  };

  return (
    <>
      <Input
        context
        disabled={isLocked}
        inputType={SELECT}
        label={label}
        onSISelect={(e) => {
          const { value } = e.detail;
          autoOpen(value);
          onSIChange(e);
        }}
        ref={inputRef}
        value={value}
      >
        <SelectOption value={SHADOW_STYLE_NONE}>None</SelectOption>
        <SelectOption value={SHADOW_STYLE_OUTER}>Outer</SelectOption>
        {!disableInsetShadow && <SelectOption value={SHADOW_STYLE_INNER}>Inner</SelectOption>}
        {![SHADOW_STYLE_NONE, undefined, MIXED].includes(value) && (
          <Button
            data-testid="drop-shadow-flyout-button"
            slot="suffix"
            variant="icon"
            iconPrefix="mix"
            onSIClick={() => flyoutRef.current.showPopover(inputRef.current)}
          />
        )}
      </Input>
      <Flyout ref={flyoutRef} title="Drop Shadow" position="top" flip="false" shift={shift}>
        <Box pb="2">
          {/* todo: shadow sub props */}
          {Object.values(subProps).map((subProp) => (
            <DynamicInput key={subProp.name} propData={{ ...subProp }} layout={layout} />
          ))}
        </Box>
      </Flyout>
    </>
  );
};

export default DropShadowProperties;
